import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from '../../utils/format'

interface ThreatActivityChartProps {
  data: {
    timestamp: Date;
    anomalyScore: number;
    networkActivity: number;
    suspiciousConnections: number;
    authenticationFailures: number;
    unauthorizedAccess: number;
  }[];
  height?: number;
}

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-console-surface/95 border border-console-border rounded-lg p-3 shadow-xl backdrop-blur-sm">
        <p className="text-console-text font-medium text-sm mb-2">
          {label}
        </p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center justify-between gap-4 text-xs">
            <span style={{ color: entry.color }}>{entry.name}:</span>
            <span className="font-medium text-console-text">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const ThreatActivityChart: React.FC<ThreatActivityChartProps> = ({ data, height = 256 }) => {
  // Transform data for recharts
  const chartData = data.map((d) => {
    const timestamp = d.timestamp instanceof Date ? d.timestamp : new Date(d.timestamp);
    return {
      ...d,
      timestamp: timestamp.getTime(), // Use timestamp for sorting
      timeLabel: format.absoluteTime(timestamp, 'HH:mm'),
    };
  });

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
        <XAxis
          dataKey="timeLabel"
          stroke="#64748b"
          fontSize={11}
          tick={{ fill: '#64748b' }}
        />
        <YAxis
          yAxisId="left"
          stroke="#64748b"
          fontSize={11}
          tick={{ fill: '#64748b' }}
          label={{ value: 'Score / Connections', angle: -90, position: 'insideLeft', style: { fill: '#64748b' } }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          stroke="#64748b"
          fontSize={11}
          tick={{ fill: '#64748b' }}
          label={{ value: 'Network Activity', angle: 90, position: 'insideRight', style: { fill: '#64748b' } }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{
            paddingTop: '20px',
            fontSize: '12px',
          }}
          iconType="line"
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="anomalyScore"
          name="Anomaly Score"
          stroke="#ef4444"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="networkActivity"
          name="Network Activity"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="suspiciousConnections"
          name="Suspicious Connections"
          stroke="#fb923c"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};